import {Box, Tooltip, Typography} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {FC} from 'react';
import {useIntl} from 'react-intl';
import {DateBasisToggleProps} from './DateBasisToggle.types';
import {captionSx, hintSx, iconSx, optionSx, selectedOptionSx, trackSx, wrapperSx} from './DateBasisToggle.sx';

/**
 * Which end of a session the chosen period applies to, written as the sentence it is.
 *
 * A session has two timestamps and a period has two ends, so "sessions in March" is ambiguous until
 * somebody says which. This lived in the footer of the period dropdown, invisible unless you opened
 * the list and scrolled past it, so the answer was always whatever the default happened to be.
 *
 * It reads as "Counted by start time" and the two words are the control - the statement and the
 * switch are the same object, so there is nothing to label. No caption above it either: a label would
 * make this the only two-line cell in a row of 32px controls, which is what shifted the row when this
 * was first tried as a captioned toggle.
 */
export const DateBasisToggle: FC<DateBasisToggleProps> = ({value, onChange, disabled, withConsumptionCaveat}) => {
    const intl = useIntl();

    const option = (basis: 'start' | 'stop', label: string) => (
        <Box
            component="button"
            type="button"
            disabled={disabled}
            aria-pressed={value === basis}
            onClick={() => onChange(basis)}
            sx={value === basis ? selectedOptionSx : optionSx}
        >
            {label}
        </Box>
    );

    return (
        <Box sx={wrapperSx}>
            <Typography
                component="span"
                variant="caption"
                sx={captionSx}
            >
                {intl.formatMessage({id: 'by'})}
            </Typography>
            <Box sx={trackSx}>
                {option('start', intl.formatMessage({id: 'byStart'}))}
                {option('stop', intl.formatMessage({id: 'byStop'}))}
            </Box>
            {withConsumptionCaveat && (
                <Tooltip title={intl.formatMessage({id: 'consumptionCountedWhenMeasuredHint'})}>
                    <Box
                        component="span"
                        sx={hintSx}
                        tabIndex={0}
                        aria-label={intl.formatMessage({id: 'consumptionCountedWhenMeasuredHint'})}
                    >
                        <InfoOutlinedIcon sx={iconSx}/>
                    </Box>
                </Tooltip>
            )}
        </Box>
    );
};
